import { AnimatePresence, motion } from "framer-motion";
import { Lock, ShieldCheck } from "lucide-react";
import { useEffect, useState } from "react";

/**
 * Full-screen "preparing checkout" overlay.
 *
 * Shown the moment the shopper taps Checkout and stays mounted until the
 * browser leaves the page for Stripe (`window.location.href = …`). The old
 * "Redirecting…" button-text was too subtle on bright mobile screens — users
 * were frantically tapping thinking the site had frozen, then bailing.
 *
 * This overlay solves it by owning the entire viewport with constant motion
 * (animated spinner + progress bar) so there's zero ambiguity that something
 * is happening. After 6s we switch the subtext to a "taking longer than
 * usual" note. After 10s we also expose a "Reload" escape hatch so a slow
 * Stripe load doesn't trap the shopper — tapping it reloads the current
 * page (which cleanly re-prepares a fresh checkout attempt).
 */
export default function CheckoutRedirectOverlay({ visible, totalLabel }) {
  const [slowNetwork, setSlowNetwork] = useState(false);
  const [showEscape, setShowEscape] = useState(false);
  useEffect(() => {
    if (!visible) { setSlowNetwork(false); setShowEscape(false); return; }
    const slow = setTimeout(() => setSlowNetwork(true), 6000);
    const esc = setTimeout(() => setShowEscape(true), 10000);
    return () => { clearTimeout(slow); clearTimeout(esc); };
  }, [visible]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          data-testid="checkout-redirect-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-tracksy-ink/95 px-6 text-white backdrop-blur-sm"
        >
          <motion.div
            initial={{ scale: 0.92, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.05, type: "spring", stiffness: 240, damping: 22 }}
            className="relative"
          >
            <div className="h-20 w-20 rounded-full border-4 border-white/15 border-t-tracksy-orange animate-spin" />
            <Lock className="absolute inset-0 m-auto h-7 w-7 text-tracksy-orange" />
          </motion.div>

          <motion.div
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="mt-7 text-center"
          >
            <p className="font-heading text-2xl font-black tracking-tight">
              Preparing your secure checkout…
            </p>
            {totalLabel && (
              <p className="mt-2 text-sm font-semibold text-white/70">
                {totalLabel}
              </p>
            )}
            <p
              data-testid="checkout-redirect-subtext"
              className="mt-5 flex items-center justify-center gap-2 text-xs font-medium text-white/60"
            >
              <ShieldCheck className="h-3.5 w-3.5 text-tracksy-green" />
              {slowNetwork
                ? "Taking a little longer than usual — hang tight, don't close this tab."
                : "Encrypting your cart with Stripe · Shop Pay · Apple Pay"}
            </p>
          </motion.div>

          {/* Indeterminate progress bar — guarantees constant motion even if
              the spinner animation is suppressed by reduced-motion settings. */}
          <div className="mt-10 h-1 w-full max-w-xs overflow-hidden rounded-full bg-white/10">
            <motion.div
              className="h-full w-1/3 rounded-full bg-tracksy-orange"
              animate={{ x: ["-100%", "300%"] }}
              transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
            />
          </div>

          {/* After 10s, surface an escape hatch so the shopper never feels
              trapped. Tapping it reloads the current page (keeping the cart
              in state since we persist to localStorage) and lets them retry. */}
          <AnimatePresence>
            {showEscape && (
              <motion.button
                data-testid="checkout-redirect-escape"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                onClick={() => window.location.reload()}
                className="mt-8 rounded-full border border-white/20 bg-white/5 px-5 py-2 text-xs font-semibold text-white/80 transition hover:bg-white/10 hover:text-white"
              >
                Stuck? Tap to retry
              </motion.button>
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
